/** Cross-UI bridge: open the assistant and optionally auto-send a prompt. */

export const ASSISTANT_GUIDE_EVENT = 'comforthub:assistant-guide'

export interface AssistantGuideDetail {
  prompt: string
  autoSend?: boolean
  /**
   * Human title for the session when the prompt is auto-sent. Guided prompts
   * are long instructions; without this the server auto-titles the chat from
   * the prompt text ("Introduce yourself as the ComfortHub backoffice…"),
   * which floods the history list. See F1 in the 2026-08-01 test report.
   */
  title?: string
}

type GuideListener = (detail: AssistantGuideDetail) => void

let pendingGuide: AssistantGuideDetail | null = null

export function isAssistantGuideDetail(value: unknown): value is AssistantGuideDetail {
  if (!value || typeof value !== 'object') return false
  const prompt = (value as AssistantGuideDetail).prompt
  return typeof prompt === 'string' && prompt.trim().length > 0
}

/** Queue a guide request and notify listeners (Shell / AssistantPanel). */
export function requestAssistantGuide(detail: AssistantGuideDetail): void {
  const prompt = detail.prompt.trim()
  if (!prompt) return
  const next: AssistantGuideDetail = {
    prompt,
    autoSend: Boolean(detail.autoSend),
    title: detail.title?.trim() || undefined,
  }
  pendingGuide = next
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent<AssistantGuideDetail>(ASSISTANT_GUIDE_EVENT, { detail: next }),
  )
}

/** Read + clear a guide that was requested before the panel mounted. */
export function consumePendingAssistantGuide(): AssistantGuideDetail | null {
  const next = pendingGuide
  pendingGuide = null
  return next
}

/** Peek without clearing (tests / Shell open-before-consume). */
export function peekPendingAssistantGuide(): AssistantGuideDetail | null {
  return pendingGuide
}

export function clearPendingAssistantGuide(): void {
  pendingGuide = null
}

export function subscribeAssistantGuide(listener: GuideListener): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<AssistantGuideDetail>).detail
    if (isAssistantGuideDetail(detail)) listener(detail)
  }
  window.addEventListener(ASSISTANT_GUIDE_EVENT, handler)
  return () => window.removeEventListener(ASSISTANT_GUIDE_EVENT, handler)
}
